import {
  ConnectNotificationBanner,
  ConnectPayouts,
} from "@stripe/react-connect-js";
import { createFileRoute } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { PageWrapper } from "~/components/page-wrapper";
import { ConnectProvider } from "~/components/stripe/connect-provider";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";

export const Route = createFileRoute("/_authenticated/payouts")({
  component: PayoutsPage,
  pendingComponent: () => (
    <PageWrapper
      description="View your payout schedule and history"
      title="Payouts"
    >
      <div className="bg-card animate-pulse rounded-xl border">
        <div className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <div className="bg-muted h-6 w-48 rounded-md" />
            <div className="bg-muted h-9 w-32 rounded-md" />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div className="border-border rounded-lg border p-4" key={i}>
                <div className="bg-muted h-4 w-20 rounded" />
                <div className="bg-muted mt-2 h-8 w-32 rounded" />
              </div>
            ))}
          </div>
          <div className="space-y-3 pt-4">
            {[1, 2, 3, 4].map((i) => (
              <div className="flex gap-4 py-3" key={i}>
                <div className="bg-muted h-4 w-24 rounded" />
                <div className="bg-muted h-4 w-32 rounded" />
                <div className="bg-muted h-4 flex-1 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageWrapper>
  ),
});

function PayoutsPage() {
  const { organization } = useActiveOrganization();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  if (!organization) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="View your payout schedule and history"
        title="Payouts"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="View your payout schedule and history"
        title="Payouts"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view payouts
  if (!canViewIntegrations) {
    return (
      <PageWrapper
        description="View your payout schedule and history"
        title="Payouts"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view payout information. Contact your
            manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      description="Track your payout schedule and view past payouts"
      title="Payouts"
    >
      <ConnectProvider organizationId={organization.id}>
        <ConnectNotificationBanner />
      </ConnectProvider>
      <ConnectProvider
        className="bg-card overflow-hidden rounded-xl border p-6"
        organizationId={organization.id}
      >
        <ConnectPayouts />
      </ConnectProvider>
    </PageWrapper>
  );
}
